import {BehaviorSubject, ReplaySubject, combineLatest, Subject, defer} from 'rxjs';
import {filter, map, take, switchMap, switchMapTo, withLatestFrom, tap, shareReplay, retry} from 'rxjs/operators';
import {FavoritesStatus} from '@px/enum/perpetual';
import {mc} from '@px/helper/mc';
import {sd} from '@px/da/sd';
import {userApi} from '@px/api/user';
import {favoriteApi} from '@px/api/market';
import {preference$, updatePreference} from './preference';
import {isLogin$} from './auth';
import {spotsMap$, contractMap$} from './config';

export const favorites$ = new ReplaySubject<Array<{symbol: string; orderNum: number}>>(1);
export const favoriteSpotSymbols$ = new BehaviorSubject<string[]>([]);
export const favoriteContractSymbols$ = new BehaviorSubject<string[]>([]);

export const updateContractFavoriteOrderFailed$ = new BehaviorSubject(false);
export const refreshContractFavoriteNotifier$ = new Subject();

combineLatest([favorites$, spotsMap$])
  .pipe(
    map(convertFavorite),
    map((favorites) => favorites.sort(compare({sortKey: 'orderNum', side: 'up'}))),
    map((symbols) => symbols.map((item) => item.symbol))
  )
  .subscribe((o) => favoriteSpotSymbols$.next(o));

combineLatest([favorites$, contractMap$])
  .pipe(
    map(convertFavorite),
    map((favorites) => favorites.sort(compare({sortKey: 'orderNum', side: 'up'}))),
    map((symbols) => symbols.map((item) => item.symbol))
  )
  .subscribe((o) => {
    favoriteContractSymbols$.next(o);
  });

function convertFavorite([favorites, tradePairsMap]) {
  return favorites.filter((item) => {
    const tradePair = tradePairsMap.get(item.symbol);
    return tradePair ? ['Listed', 'PreList', 'Suspend'].includes(tradePair.status) : false;
  });
}

// 添加收藏
mc.subscribe('symbol-favorite', 'Add', async ({symbol, type, groupIds}) => {
  const market = type === 'Spot' ? 'Spot' : 'Contract';
  await addFavorite(symbol, market, groupIds);
  sd.track('add_collection', {symbol, market, add_source_page: 'trade_page'});
});

async function addFavorite(symbol, type, groupIds) {
  const symbols = typeof symbol === 'string' ? [symbol] : [...symbol];
  const params = {symbols, groupIds};
  const {data, error} = await favoriteApi.instrumentAdd(params);
  if (data) {
    mc.send('user-groups', 'refresh');
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
}

// 移除收藏
mc.subscribe('symbol-favorite', 'Remove', async ({symbol, type, groupIds}) => {
  const market = type === 'Spot' ? 'Spot' : 'Contract';
  sd.track('delete_collection', {symbol, market, delete_source_page: 'trade_page'});
  await delFavorite(symbol, groupIds);
});

async function delFavorite(symbol, groupIds) {
  const params = {symbols: [symbol], groupIds};
  const {data, error} = await favoriteApi.instrumentDelete(params);
  if (data) {
    mc.send('user-groups', 'refresh');
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
}

function compare(sorts: {sortKey: string; side: 'down' | 'up'}) {
  return function (a, b) {
    const {sortKey, side} = sorts;
    const value1 = a[sortKey];
    const value2 = b[sortKey];
    return side === 'down' ? value2 - value1 : value1 - value2;
  };
}

export function resolveSymbolNewOrders({symbols, symbol, orderNum}) {
  const originIndex = symbols.findIndex((symbolItem) => symbolItem === symbol);
  const orderNumIsValid = orderNum > -1 && orderNum < symbols.length;
  if (originIndex < 0 || !orderNumIsValid) {
    return symbols;
  }

  const newSymbols = [...symbols];
  newSymbols.splice(orderNum, 0, newSymbols.splice(originIndex, 1)[0]);

  return newSymbols;
}

function submitFavoriteOrderToRemote(symbols) {
  const symbolList = symbols.map((symbol, orderNum) => ({symbol, orderNum}));
  return isLogin$.pipe(
    filter(Boolean),
    switchMap(() => userApi.updateContractFavoriteOrder({symbolList})),
    tap((response) => {
      const error = response?.error;
      if (error) {
        updateContractFavoriteOrderFailed$.next(true);
        mc.send('toast', 'error', error);
      }
    })
  );
}

export const favoritesViewStatus$ = isLogin$.pipe(
  filter(Boolean),
  switchMapTo(preference$),
  map((data) => transferFavoritesStatus(data.contractFavoritesTopTabBar) as FavoritesStatus)
);

export function updateFavoritesViewStatus(status: FavoritesStatus) {
  return isLogin$
    .pipe(
      filter(Boolean),
      switchMap(() => updatePreference('contractFavoritesTopTabBar', status === FavoritesStatus.Visible))
    )
    .pipe(take(1))
    .subscribe();
}

type TransferValue<T extends boolean | FavoritesStatus> = T extends boolean ? FavoritesStatus : boolean;
export function transferFavoritesStatus<T extends boolean | FavoritesStatus>(status: T): TransferValue<T> {
  if (typeof status === 'boolean') {
    return (status ? FavoritesStatus.Visible : FavoritesStatus.Hidden) as TransferValue<T>;
  }

  return (status === FavoritesStatus.Visible) as TransferValue<T>;
}

// drag-library
const loadDndAction$ = defer(() => import('@px/helper/libs/dnd-action')).pipe(retry(3), shareReplay(1));
export const dndAction$ = loadDndAction$.pipe(map((dndItem) => dndItem.dndzone));

// drag-sort
export const moveContractMarketNotifier$: Subject<{symbol: string; orderNum: number}> = new Subject();
moveContractMarketNotifier$
  .pipe(
    withLatestFrom(favoriteContractSymbols$),
    map(([{symbol, orderNum}, symbols]) => resolveSymbolNewOrders({symbols, symbol, orderNum})),
    switchMap((sortedSymbols) => submitFavoriteOrderToRemote(sortedSymbols)),
    tap(() => {
      refreshContractFavoriteNotifier$.next(true);
    })
  )
  .subscribe();

export function moveContractMarket(moveItem: {symbol: string; orderNum: number}) {
  moveContractMarketNotifier$.next(moveItem);
}

// drag-sort
export const moveSpotMarketNotifier$: Subject<{symbol: string; orderNum: number}> = new Subject();
moveSpotMarketNotifier$
  .pipe(
    withLatestFrom(favoriteSpotSymbols$),
    map(([{symbol, orderNum}, symbols]) => resolveSymbolNewOrders({symbols, symbol, orderNum})),
    switchMap((sortedSymbols) => submitFavoriteOrderToRemote(sortedSymbols)),
    tap(() => {
      refreshContractFavoriteNotifier$.next(true);
    })
  )
  .subscribe();

export function moveSpotMarket(moveItem: {symbol: string; orderNum: number}) {
  moveSpotMarketNotifier$.next(moveItem);
}

// 刷新收藏夹数据
refreshContractFavoriteNotifier$.pipe(switchMap(() => isLogin$.pipe(tap(() => mc.send('user-groups', 'refresh'))))).subscribe();
